import { z } from 'zod';
import {
  iconnectSyncConfigSchema,
  workflowIntroConfigSchema,
  type WorkflowIntroConfig,
} from './form-workflows.js';

// Settings that describe how one form behaves wherever it is used. They started on the workflow (intro config,
// messages, metadata) and move onto the form, so a form behaves the same in every workflow that uses it.
// Stored as JSON on the form and parsed on read, so a form with nothing stored gets the defaults below.

/** How a person starts filling in the form. */
export const formIntakeSchema = z.object({
  warningMessage: z.string().prefault(''),
  /** The sentence above the fields of a card that already holds values. Off means there is none. */
  showPrefilledMessage: z.boolean().prefault(true),
  /** Words that sentence in place of the usual one. Empty or missing keeps the usual one. */
  prefilledMessage: z.string().optional(),
  showSpeechOption: z.boolean().prefault(false),
  showManualButton: z.boolean().prefault(true),
  showFileUploadOption: z.boolean().prefault(false),
  showCancelButton: z.boolean().prefault(true),
  extractionPrompt: z.string().optional(),
  disclaimerEnabled: z.boolean().prefault(false),
  disclaimerTitle: z.string().optional(),
  disclaimerText: z.string().optional(),
  introResponse: workflowIntroConfigSchema.shape.introResponse,
  dashboardElement: workflowIntroConfigSchema.shape.dashboardElement,
});

/** Where a submitted form is sent. */
export const formDeliverySchema = z.object({
  sendToEndpoint: z.boolean().prefault(false),
  dataConnectionIdentifier: z.string().optional(),
  endpoint: z.string().optional(),
});

/**
 * The most characters the label of a form's submit button may have when it is set. It is drawn inside a button on the form card, so a longer
 * one would not fit. It is held on entry only: a label stored before the limit existed is still read and shown as it is.
 */
export const SUBMIT_BUTTON_TEXT_MAX_LENGTH = 40;

/**
 * What happens when the form is submitted. Kept as one object so that, if submission later becomes its own step
 * over several forms, it can be lifted out as it is.
 */
export const formSubmissionSchema = z.object({
  submitButtonText: z.string().optional(),
  successMessage: z.string().prefault(''),
  responseMessage: z.string().prefault(''),
  /**
   * A note for the agent, shown to it on a person's next message after they submit this form: a template with a field's key between
   * double braces (see `form-agent-note.ts`). Empty means the agent is told only that the form was submitted. It cannot go with
   * `delivery.sendToEndpoint`, which is checked when the form is saved.
   */
  agentNote: z.string().prefault(''),
  delivery: formDeliverySchema.prefault({}),
  iconnectSync: iconnectSyncConfigSchema.optional(),
});

/** What is stored: any field may be missing. Parse with the schema to get the defaults filled in. */
export type FormIntakeInput = z.input<typeof formIntakeSchema>;
export type FormSubmissionInput = z.input<typeof formSubmissionSchema>;

export type FormIntake = z.infer<typeof formIntakeSchema>;
export type FormDelivery = z.infer<typeof formDeliverySchema>;
export type FormSubmission = z.infer<typeof formSubmissionSchema>;

/** The intro config the chat client reads, put back together from a form's intake and submission. */
export function introConfigFromFormSettings(intake: FormIntake, submission: FormSubmission): WorkflowIntroConfig {
  return {
    ...intake,
    submitButtonText: submission.submitButtonText,
    iconnectSync: submission.iconnectSync,
  };
}
